import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Tab,
  Tabs,
  Stack,
  Typography,
} from '@mui/material';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import { alpha } from '@mui/material/styles';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';
import { useTranslation } from 'react-i18next';
import {
  archiveNotice,
  fetchNotices,
  markAllNoticesRead,
  markNoticeRead,
  unarchiveNotice,
} from '../api/notices';
import { respondToCompanionRequest } from '../api/companions';
import { acceptNightInvitation, declineNightInvitation } from '../api/nights';
import { formatDisplayDate, formatDisplayTime, timeInputValueFromDateTime } from '../i18n/formatters';
import { useToast } from '../components/tavern/ToastProvider';
import {
  companionRequestResponseMessage,
  nightInvitationResponseMessage,
} from '../utils/apiErrorMessages';

const PAGE_SIZE = 12;

const noticeIcon = (type) => {
  const noticeType = typeof type === 'string' ? type : 'unknown';

  if (noticeType === 'night.created') return EventNoteOutlinedIcon;
  if (noticeType === 'night.updated') return UpdateOutlinedIcon;
  if (noticeType === 'night.invitation_accepted') return CheckCircleIcon;
  if (noticeType === 'night.invitation_declined') return EventBusyOutlinedIcon;
  if (noticeType === 'night.deleted') return EventBusyOutlinedIcon;
  if (noticeType.startsWith('companion.')) return GroupAddOutlinedIcon;
  return CampaignOutlinedIcon;
};

const noticeCopy = (notice, t, language) => {
  const payload = notice.payload || {};
  const nightTitle = payload.nightTitle || t('notices.types.fallbackTitle');
  const actorName = payload.actorName || notice.actor?.displayName || t('profile.defaults.displayName');
  const time = payload.scheduledAt
    ? `${formatDisplayDate(payload.scheduledAt, language)} - ${formatDisplayTime(timeInputValueFromDateTime(payload.scheduledAt), language)}`
    : '';
  const values = { actorName, nightTitle, time };

  if (notice.type === 'night.created') {
    return {
      title: t('notices.types.nightCreatedTitle', values),
      body: t('notices.types.nightCreatedBody', values),
    };
  }

  if (notice.type === 'night.updated') {
    return {
      title: t('notices.types.nightUpdatedTitle', values),
      body: t('notices.types.nightUpdatedBody', values),
    };
  }

  if (notice.type === 'night.deleted') {
    return {
      title: t('notices.types.nightDeletedTitle', values),
      body: payload.message
        ? `${t('notices.types.nightDeletedBody', values)} ${t('notices.types.nightDeletedMessage', { message: payload.message })}`
        : t('notices.types.nightDeletedBody', values),
    };
  }

  if (notice.type === 'night.invitation_accepted') {
    return {
      title: t('notices.types.nightInvitationAcceptedTitle', values),
      body: t('notices.types.nightInvitationAcceptedBody', values),
    };
  }

  if (notice.type === 'night.invitation_declined') {
    return {
      title: t('notices.types.nightInvitationDeclinedTitle', values),
      body: t('notices.types.nightInvitationDeclinedBody', values),
    };
  }

  if (notice.type === 'companion.requested') {
    return {
      title: t('notices.types.companionRequestedTitle', values),
      body: t('notices.types.companionRequestedBody', values),
    };
  }

  if (notice.type === 'companion.accepted') {
    return {
      title: t('notices.types.companionAcceptedTitle', values),
      body: t('notices.types.companionAcceptedBody', values),
    };
  }

  if (notice.type === 'companion.declined') {
    return {
      title: t('notices.types.companionDeclinedTitle', values),
      body: t('notices.types.companionDeclinedBody', values),
    };
  }

  return {
    title: t('notices.types.fallbackTitle'),
    body: t('notices.types.fallbackBody'),
  };
};

const ARCHIVE_SWIPE_THRESHOLD = 96;
const NOTICE_STATUS_ACTIVE = 'active';
const NOTICE_STATUS_ARCHIVED = 'archived';

const safeNoticeType = (notice) =>
  typeof notice?.type === 'string' && notice.type ? notice.type : 'unknown';

const safeNoticePayload = (payload) =>
  payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {};

const normalizeNotice = (notice) => {
  const id = Number(notice?.id);
  if (!Number.isInteger(id) || id <= 0) return null;

  const createdAt =
    notice?.createdAt && !Number.isNaN(new Date(notice.createdAt).getTime())
      ? notice.createdAt
      : new Date().toISOString();

  return {
    ...notice,
    id,
    type: safeNoticeType(notice),
    payload: safeNoticePayload(notice?.payload),
    readAt: notice?.readAt || null,
    archivedAt: notice?.archivedAt || null,
    createdAt,
    actor: notice?.actor && typeof notice.actor === 'object' ? notice.actor : null,
  };
};

const normalizeNotices = (notices = []) => notices.map(normalizeNotice).filter(Boolean);

const noticeTimestamp = (notice) => {
  const timestamp = new Date(notice?.createdAt).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const sortNoticesByCreation = (notices) =>
  [...notices].sort((left, right) => noticeTimestamp(right) - noticeTimestamp(left));

const isOpenResponseStatus = (status) =>
  !['accepted', 'declined', 'unavailable'].includes(String(status || ''));

const restoreNoticeInList = (notice, current) => {
  const normalized = normalizeNotice(notice);
  if (!normalized || current.some((item) => item.id === normalized.id)) return current;

  return sortNoticesByCreation([normalized, ...current]);
};

const NoticeCard = ({
  copy,
  Icon,
  language,
  notice,
  responseLoading,
  onArchive,
  onAcceptInvitation,
  onDeclineInvitation,
  onOpen,
  onUnarchive,
  t,
  unread,
}) => {
  const [dragX, setDragX] = useState(0);
  const dragStartRef = useRef(null);
  const dragXRef = useRef(0);
  const didDragRef = useRef(false);

  const resetDrag = () => {
    dragStartRef.current = null;
    dragXRef.current = 0;
    setDragX(0);
  };
  const canArchive = !notice.archivedAt;
  const archiveRevealProgress = canArchive
    ? Math.min(Math.abs(dragX) / ARCHIVE_SWIPE_THRESHOLD, 1)
    : 0;
  const archiveActionVisible = archiveRevealProgress > 0;
  const canRespondToNightInvitation =
    notice.type === 'night.created' &&
    notice.payload?.nightId &&
    isOpenResponseStatus(notice.payload?.invitationStatus);
  const canRespondToCompanionInvitation =
    notice.type === 'companion.requested' &&
    notice.payload?.friendshipId &&
    isOpenResponseStatus(notice.payload?.friendshipStatus);
  const canRespond = canRespondToNightInvitation || canRespondToCompanionInvitation;

  const capturePointer = (event) => {
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      // Some mobile WebViews expose pointer capture but reject it for touch gestures.
    }
  };

  const releasePointer = (event) => {
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch {
      // The pointer may already be released when the card is removed optimistically.
    }
  };

  const handlePointerDown = (event) => {
    if (!canArchive) return;
    if (event.button && event.button !== 0) return;

    capturePointer(event);
    dragStartRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
    didDragRef.current = false;
  };

  const handlePointerMove = (event) => {
    if (!dragStartRef.current || dragStartRef.current.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - dragStartRef.current.x;
    const deltaY = event.clientY - dragStartRef.current.y;

    if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
    if (Math.abs(deltaY) > Math.abs(deltaX)) return;

    event.preventDefault();
    const nextDragX = Math.max(Math.min(deltaX, 0), -132);
    dragXRef.current = nextDragX;
    didDragRef.current = Math.abs(nextDragX) > 12;
    setDragX(nextDragX);
  };

  const handlePointerUp = (event) => {
    if (!dragStartRef.current || dragStartRef.current.pointerId !== event.pointerId) return;

    releasePointer(event);
    dragStartRef.current = null;

    if (canArchive && dragXRef.current <= -ARCHIVE_SWIPE_THRESHOLD) {
      resetDrag();
      onArchive(notice);
      return;
    }

    resetDrag();
  };

  const handleOpen = () => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }

    onOpen(notice);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
        bgcolor: archiveActionVisible ? 'primary.main' : 'transparent',
      }}
    >
      {archiveActionVisible ? (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
          sx={{
            position: 'absolute',
            inset: 0,
            px: 2,
            color: 'primary.contrastText',
            opacity: archiveRevealProgress,
            pointerEvents: 'none',
            transform: `translateX(${Math.round((1 - archiveRevealProgress) * 12)}px)`,
            transition: dragX ? 'none' : 'opacity 160ms ease, transform 160ms ease',
          }}
        >
          <ArchiveOutlinedIcon />
          <Typography fontWeight={900}>{t('notices.actions.archive')}</Typography>
        </Stack>
      ) : null}
      <Card
        variant="outlined"
        component="article"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={resetDrag}
        sx={{
          width: '100%',
          textAlign: 'left',
          borderRadius: 3,
          borderColor: unread ? 'secondary.main' : 'divider',
          bgcolor: unread ? alpha('#ECAA41', 0.08) : 'background.paper',
          transform: `translateX(${dragX}px)`,
          touchAction: canArchive ? 'pan-y' : 'auto',
          userSelect: dragX ? 'none' : 'auto',
          transition: dragX ? 'none' : 'border-color 160ms ease, transform 160ms ease',
          '&:hover': {
            borderColor: 'primary.main',
            transform: dragX ? `translateX(${dragX}px)` : 'translateY(-1px)',
          },
        }}
      >
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Badge
              color="secondary"
              variant="dot"
              invisible={!unread}
              overlap="circular"
            >
              <Avatar src={notice.actor?.avatarUrl || undefined}>
                <Icon />
              </Avatar>
            </Badge>
            <Box
              component="div"
              role="button"
              aria-label={copy.title}
              tabIndex={0}
              onClick={handleOpen}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleOpen();
                }
              }}
              sx={{
                flex: 1,
                minWidth: 0,
                p: 0,
                border: 0,
                bgcolor: 'transparent',
                color: 'inherit',
                cursor: 'pointer',
                font: 'inherit',
                textAlign: 'left',
                '&:focus-visible': {
                  outline: '3px solid',
                  outlineColor: 'secondary.light',
                  outlineOffset: 3,
                  borderRadius: 1,
                },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 0.5, flexWrap: 'wrap' }}
              >
                <Typography variant="h3" sx={{ fontSize: { xs: 18, md: 20 } }}>
                  {copy.title}
                </Typography>
                {unread ? (
                  <Chip
                    size="small"
                    label={t('notices.unread')}
                    color="secondary"
                    sx={{ fontWeight: 800 }}
                  />
                ) : null}
              </Stack>
              <Typography color="text.secondary">{copy.body}</Typography>
              {canRespond ? (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{ mt: 1.5 }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    disabled={responseLoading}
                    onClick={(event) => {
                      event.stopPropagation();
                      onAcceptInvitation(notice);
                    }}
                    sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                  >
                    {responseLoading ? t('notices.actions.answering') : t('notices.actions.acceptInvitation')}
                  </Button>
                  <Button
                    variant="outlined"
                    disabled={responseLoading}
                    onClick={(event) => {
                      event.stopPropagation();
                      onDeclineInvitation(notice);
                    }}
                    sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                  >
                    {t('notices.actions.declineInvitation')}
                  </Button>
                </Stack>
              ) : null}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {formatDisplayDate(notice.createdAt, language)} - {formatDisplayTime(timeInputValueFromDateTime(notice.createdAt), language)}
              </Typography>
            </Box>
            <IconButton
              aria-label={
                canArchive
                  ? t('notices.actions.archiveNotice', { title: copy.title })
                  : t('notices.actions.unarchiveNotice', { title: copy.title })
              }
              onClick={(event) => {
                event.stopPropagation();
                if (canArchive) {
                  onArchive(notice);
                } else {
                  onUnarchive(notice);
                }
              }}
              sx={{
                mt: -0.5,
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'rgba(15, 76, 69, 0.08)',
                  color: 'primary.main',
                },
              }}
            >
              {canArchive ? <ArchiveOutlinedIcon /> : <UnarchiveOutlinedIcon />}
            </IconButton>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

const NoticesPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { i18n, t } = useTranslation();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(NOTICE_STATUS_ACTIVE);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [respondingNoticeId, setRespondingNoticeId] = useState(null);
  const noticeMutationsRef = useRef(new Set());

  const language = i18n.resolvedLanguage || i18n.language;
  const viewingArchive = status === NOTICE_STATUS_ARCHIVED;
  const hasUnread = useMemo(
    () => !viewingArchive && items.some((notice) => !notice.readAt),
    [items, viewingArchive]
  );

  const loadPage = useCallback(
    async (nextPage = 1, mode = 'replace') => {
      if (nextPage === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      try {
        const data = await fetchNotices({ page: nextPage, limit: PAGE_SIZE, status });
        setItems((current) =>
          mode === 'append'
            ? normalizeNotices([...current, ...(data.items || [])])
            : normalizeNotices(data.items || [])
        );
        setPage(data.page || nextPage);
        setHasMore(Boolean(data.hasMore));
      } catch {
        toast.error(t('notices.feedback.loadError'));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [status, t, toast]
  );

  useEffect(() => {
    loadPage(1);
  }, [loadPage]);

  useEffect(() => {
    const shouldReloadForNoticeEvent = (type) =>
      ['notice.created', 'notice.updated', 'notice.read', 'notice.archived'].includes(type);

    const handleServerEvent = (event) => {
      const type = event.detail?.type;
      if (shouldReloadForNoticeEvent(type)) {
        const noticeId = Number(event.detail?.noticeId);
        if (Number.isInteger(noticeId) && noticeMutationsRef.current.has(noticeId)) {
          return;
        }

        loadPage(1);
      }
    };
    const handleRevalidate = () => loadPage(1);

    window.addEventListener('thetavern:server-event', handleServerEvent);
    window.addEventListener('thetavern:revalidate', handleRevalidate);
    return () => {
      window.removeEventListener('thetavern:server-event', handleServerEvent);
      window.removeEventListener('thetavern:revalidate', handleRevalidate);
    };
  }, [loadPage]);

  const trackNoticeMutation = (noticeId) => {
    if (!Number.isInteger(noticeId) || noticeId <= 0) return () => {};

    noticeMutationsRef.current.add(noticeId);
    let cleared = false;

    return () => {
      if (cleared) return;
      cleared = true;
      window.setTimeout(() => {
        noticeMutationsRef.current.delete(noticeId);
      }, 1200);
    };
  };

  const handleMarkAllRead = async () => {
    if (viewingArchive) return;

    try {
      await markAllNoticesRead();
      setItems((current) =>
        current.map((notice) => ({
          ...notice,
          readAt: notice.readAt || new Date().toISOString(),
        }))
      );
    } catch {
      toast.error(t('notices.feedback.readError'));
    }
  };

  const handleOpenNotice = async (notice) => {
    const type = safeNoticeType(notice);

    if (!notice.readAt) {
      try {
        await markNoticeRead(notice.id);
        setItems((current) =>
          current.map((item) =>
            item.id === notice.id ? { ...item, readAt: new Date().toISOString() } : item
          )
        );
      } catch {
        toast.error(t('notices.feedback.readError'));
      }
    }

    if (notice.payload?.nightId && type !== 'night.deleted') {
      const focusParam = type === 'night.created' ? 'focusNightId' : 'highlightNight';

      navigate(`/nights?${focusParam}=${notice.payload.nightId}`);
      return;
    }

    if (type.startsWith('companion.')) {
      navigate('/friends');
      return;
    }

    navigate('/nights');
  };

  const handleInvitationResponse = async (notice, action) => {
    const noticeType = safeNoticeType(notice);
    const nightId = Number(notice?.payload?.nightId);
    const friendshipId = Number(notice?.payload?.friendshipId);
    const isNightInvitation = noticeType === 'night.created';
    const isCompanionInvitation = noticeType === 'companion.requested';

    if (respondingNoticeId) return;
    if (
      isNightInvitation &&
      (!Number.isInteger(nightId) || nightId <= 0)
    ) {
      return;
    }
    if (
      isCompanionInvitation &&
      (!Number.isInteger(friendshipId) || friendshipId <= 0)
    ) {
      return;
    }
    if (!isNightInvitation && !isCompanionInvitation) return;

    setRespondingNoticeId(notice.id);
    try {
      if (isNightInvitation && action === 'accept') {
        await acceptNightInvitation(nightId);
        toast.success(t('nights.feedback.invitationAccepted'));
      } else if (isNightInvitation) {
        await declineNightInvitation(nightId);
        toast.success(t('nights.feedback.invitationDeclined'));
      } else if (action === 'accept') {
        await respondToCompanionRequest(friendshipId, 'accepted');
        toast.success(t('friends.requests.accepted'));
      } else {
        await respondToCompanionRequest(friendshipId, 'declined');
        toast.success(t('friends.requests.declined'));
      }

      setItems((current) =>
        current.map((item) =>
          item.id === notice.id
            ? {
                ...item,
                payload: {
                  ...(item.payload || {}),
                  ...(isNightInvitation
                    ? { invitationStatus: action === 'accept' ? 'accepted' : 'declined' }
                    : { friendshipStatus: action === 'accept' ? 'accepted' : 'declined' }),
                },
                readAt: item.readAt || new Date().toISOString(),
              }
            : item
        )
      );
    } catch (error) {
      toast.error(
        isNightInvitation
          ? nightInvitationResponseMessage(error, t)
          : companionRequestResponseMessage(error, t)
      );
      if ([404, 409].includes(Number(error?.response?.status))) {
        setItems((current) =>
          current.map((item) =>
            item.id === notice.id
              ? {
                ...item,
                payload: {
                  ...(item.payload || {}),
                  ...(isNightInvitation
                    ? { invitationStatus: 'unavailable' }
                    : { friendshipStatus: 'unavailable' }),
                },
                readAt: item.readAt || new Date().toISOString(),
              }
            : item
          )
        );
        loadPage(1);
      }
    } finally {
      setRespondingNoticeId(null);
    }
  };

  const handleArchiveNotice = async (notice) => {
    if (notice.archivedAt) return;

    const stopTrackingMutation = trackNoticeMutation(Number(notice.id));
    setItems((current) => current.filter((item) => item.id !== notice.id));

    try {
      await archiveNotice(notice.id);
      toast.success(t('notices.feedback.archived'));
    } catch {
      setItems((current) => restoreNoticeInList(notice, current));
      toast.error(t('notices.feedback.archiveError'));
    } finally {
      stopTrackingMutation();
    }
  };

  const handleUnarchiveNotice = async (notice) => {
    if (!notice.archivedAt) return;

    const stopTrackingMutation = trackNoticeMutation(Number(notice.id));
    setItems((current) => current.filter((item) => item.id !== notice.id));

    try {
      await unarchiveNotice(notice.id);
      toast.success(t('notices.feedback.unarchived'));
    } catch {
      setItems((current) => restoreNoticeInList(notice, current));
      toast.error(t('notices.feedback.unarchiveError'));
    } finally {
      stopTrackingMutation();
    }
  };

  const handleStatusChange = (_event, nextStatus) => {
    setStatus(nextStatus);
    setItems([]);
    setPage(1);
    setHasMore(false);
  };

  return (
    <Stack spacing={4}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-end' }}
        spacing={2}
      >
        <Box>
          <Typography variant="subtitle2" color="secondary.dark">
            {t('notices.eyebrow')}
          </Typography>
          <Typography variant="h1">{t('notices.title')}</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {t('notices.subtitle')}
          </Typography>
        </Box>
        <Button
          disabled={viewingArchive || !hasUnread}
          startIcon={<DoneAllIcon />}
          onClick={handleMarkAllRead}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
        >
          {t('notices.actions.markAllRead')}
        </Button>
      </Stack>

      <Tabs
        value={status}
        onChange={handleStatusChange}
        variant="fullWidth"
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTab-root': {
            minHeight: 44,
            fontWeight: 900,
          },
        }}
      >
        <Tab value={NOTICE_STATUS_ACTIVE} label={t('notices.tabs.active')} />
        <Tab value={NOTICE_STATUS_ARCHIVED} label={t('notices.tabs.archived')} />
      </Tabs>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 8 }}>
          <CircularProgress color="secondary" />
        </Stack>
      ) : items.length === 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: { xs: 3, md: 5 }, textAlign: 'center' }}>
            <Avatar
              sx={{
                mx: 'auto',
                mb: 2,
                width: 64,
                height: 64,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              <CampaignOutlinedIcon />
            </Avatar>
            <Typography variant="h3">
              {viewingArchive ? t('notices.archiveEmptyTitle') : t('notices.emptyTitle')}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              {viewingArchive ? t('notices.archiveEmptySubtitle') : t('notices.emptySubtitle')}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {items.map((notice) => {
            const Icon = noticeIcon(safeNoticeType(notice));
            const copy = noticeCopy(notice, t, language);
            const unread = !notice.readAt;

            return (
              <NoticeCard
                key={notice.id}
                copy={copy}
                Icon={Icon}
                language={language}
                notice={notice}
                onArchive={handleArchiveNotice}
                onAcceptInvitation={(item) => handleInvitationResponse(item, 'accept')}
                onDeclineInvitation={(item) => handleInvitationResponse(item, 'decline')}
                onOpen={handleOpenNotice}
                onUnarchive={handleUnarchiveNotice}
                responseLoading={respondingNoticeId === notice.id}
                t={t}
                unread={unread}
              />
            );
          })}
        </Stack>
      )}

      {hasMore ? (
        <Button
          disabled={loadingMore}
          onClick={() => loadPage(page + 1, 'append')}
          sx={{ alignSelf: 'center' }}
        >
          {loadingMore ? <CircularProgress size={20} /> : t('notices.actions.loadMore')}
        </Button>
      ) : null}
    </Stack>
  );
};

export default NoticesPage;
